<template>
  <div class="raw-materials">
    <div
      v-for="type in visibleTypes"
      :key="type.key"
      class="rm-row"
    >
      <div class="rm-name">
        <span class="rm-name-text">{{ translate(type.name) }}</span>
      </div>

      <!-- Editable inputs (everything except the totalPrice prop, which lives in the fixed price column). -->
      <div class="rm-inputs">
        <div
          v-for="prop in inputProps(type)"
          :key="prop.key"
          class="rm-field"
        >
          <label class="rm-label">{{ translate(prop.label) }}</label>
          <span v-if="prop.type === 'label'" class="rm-total-value rm-input">{{ displayValue(slotOf(type.key), prop) }}</span>
          <el-input-number
            v-else
            v-model="slotOf(type.key)[prop.key]"
            :step="precision"
            :controls="false"
            :disabled="readOnly"
            class="rm-input"
          />
        </div>
      </div>

      <!-- Other calculated totals (e.g. blank weight) — sit just left of the Total-price column. -->
      <div class="rm-other-totals">
        <div
          v-for="prop in otherTotalProps(type)"
          :key="prop.key"
          class="rm-total"
        >
          <label class="rm-label">{{ translate(prop.label) }}</label>
          <span class="rm-total-value">{{ evaluate(slotOf(type.key), prop) }}</span>
        </div>
      </div>

      <!-- Fixed Total-price column — always shown for in-price types so the values align across rows. -->
      <div class="rm-total-price">
        <template v-if="type.includeInPrice !== false && totalPriceProp(type)">
          <label class="rm-label">{{ translate(totalPriceProp(type).label) }}</label>
          <span v-if="totalPriceProp(type).type === 'label'" class="rm-total-value">{{ displayValue(slotOf(type.key), totalPriceProp(type)) }}</span>
          <el-input-number
            v-else-if="totalPriceProp(type).type !== 'calculated'"
            v-model="slotOf(type.key)[totalPriceProp(type).key]"
            :step="precision"
            :controls="false"
            :disabled="readOnly"
            class="rm-input rm-price-input"
          />
          <span v-else class="rm-total-value">{{ evaluate(slotOf(type.key), totalPriceProp(type)) }}</span>
        </template>
      </div>

      <!-- Trailing remove column (empty for non-removable default types). -->
      <div class="rm-remove">
        <el-button
          v-if="!type.isDefault && !readOnly"
          type="danger"
          :icon="Delete"
          size="small"
          @click="removeType(type.key)"
        />
      </div>
    </div>

    <div v-if="!readOnly && availableTypes.length" class="rm-add">
      <el-select
        v-model="typeToAdd"
        :placeholder="translate('Select material type')"
        size="default"
        class="rm-add-select"
      >
        <el-option
          v-for="type in availableTypes"
          :key="type.key"
          :value="type.key"
          :label="translate(type.name)"
        />
      </el-select>
      <el-button type="primary" plain :disabled="!typeToAdd" @click="addType">
        {{ translate('Add material') }}
      </el-button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex'
import { markRaw } from 'vue'
import { Delete } from '@element-plus/icons-vue'

export default {
  name: 'CcRawMaterials',

  props: {
    // RawMaterialsMetadata: { title, types: [{ key, name, isDefault, includeInPrice, properties: [{ key, label, type, suffix, formula }] }] }
    metadata: { type: Object, default: () => ({ types: [] }) },
    // The component entity that holds .values.rawMaterialInfo (component itself for component/inquiry; selectedItem.component for orders).
    entity: { type: Object, default: () => ({}) },
    rootEntity: { type: Object, default: () => ({}) },
    readOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      typeToAdd: '',
      Delete: markRaw(Delete),
    }
  },

  watch: {
    entity: { handler: 'ensureStore', immediate: true },
  },

  computed: {
    ...mapGetters(['globalMetadata']),

    types() {
      return (this.metadata && this.metadata.types) || []
    },

    rawMaterialInfo() {
      return this.entity && this.entity.values ? this.entity.values.rawMaterialInfo : null
    },

    // A type is shown when it is default (always) or already has a value bag (stored earlier or just added).
    // rawMaterialInfo is non-null exactly once ensureStore has run for a defined entity, so this also
    // guards against rendering inputs before the storage path exists.
    visibleTypes() {
      const rmi = this.rawMaterialInfo
      if (!rmi) return []
      return this.types.filter((t) => t.isDefault || rmi[t.key])
    },

    // Non-default types that are not currently shown — offered in the picker.
    availableTypes() {
      const rmi = this.rawMaterialInfo
      if (!rmi) return []
      return this.types.filter((t) => !t.isDefault && !rmi[t.key])
    },

    precision() {
      if (this.globalMetadata && this.globalMetadata.precision && this.globalMetadata.precision > 0.000001) {
        return this.globalMetadata.precision
      }
      return 0.001
    },
  },

  methods: {
    // Guarantees the storage path exists and every default type has a (reactive) value bag to bind to.
    ensureStore() {
      if (!this.entity) return
      if (!this.entity.values) this.entity.values = {}
      if (!this.entity.values.rawMaterialInfo) this.entity.values.rawMaterialInfo = {}

      const rmi = this.entity.values.rawMaterialInfo
      this.types.forEach((t) => {
        if (t.isDefault && !rmi[t.key]) rmi[t.key] = {}
      })
    },

    slotOf(key) {
      const rmi = this.entity.values.rawMaterialInfo
      if (!rmi[key]) rmi[key] = {}
      return rmi[key]
    },

    // The single property that feeds the material price aggregate (priceOfMaterial sums slot.totalPrice).
    // It is rendered in the fixed Total-price column: editable when decimal (e.g. OTHERS), computed when calculated.
    totalPriceProp(type) {
      return (type.properties || []).find((p) => p.key === 'totalPrice')
    },

    // Editable inputs = decimal props other than totalPrice (which owns the Total-price column).
    inputProps(type) {
      return (type.properties || []).filter((p) => p.type !== 'calculated' && p.key !== 'totalPrice')
    },

    // Calculated totals other than totalPrice (e.g. blank weight) — shown left of the Total-price column.
    otherTotalProps(type) {
      return (type.properties || []).filter((p) => p.type === 'calculated' && p.key !== 'totalPrice')
    },

    addType() {
      if (!this.typeToAdd) return
      this.slotOf(this.typeToAdd) // creates the bag → becomes visible
      this.typeToAdd = ''
    },

    removeType(key) {
      const rmi = this.entity.values.rawMaterialInfo
      if (rmi && rmi[key]) delete rmi[key]
    },

    // Read-only display of a stored value (type === 'label'): show the value as-is, with the
    // currency/unit suffix appended, without binding an editable input.
    displayValue(slot, prop) {
      const value = slot[prop.key]
      const text = value === undefined || value === null ? '' : value
      return prop.suffix ? `${text} ${prop.suffix}` : `${text}`
    },

    // Mirrors cc-var-control's calculated-cell evaluation: eval the (already wrapped) formula with
    // `item` = the slot's value bag, write the result back so totals (priceOfMaterial) can read it,
    // then append the currency/unit suffix.
    evaluate(slot, prop) {
      const item = slot
      const rootEntity = this.rootEntity
      try {
        const result = eval(prop.formula)
        slot[prop.key] = result
        return prop.suffix ? `${result} ${prop.suffix}` : `${result}`
      } catch (e) {
        console.error('ERROR: raw material formula: ' + e.message)
        return ''
      }
    },
  },
}
</script>

<style scoped>
.raw-materials {
  width: 100%;
}

.rm-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-bottom: 1px solid #ebeef5;
}

.rm-name {
  flex: 0 0 110px;
  font-style: italic;
  font-weight: 600;
  color: #606266;
  padding-top: 18px;
}

.rm-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  flex: 1 1 auto;
  min-width: 0;
}

.rm-field {
  display: flex;
  flex-direction: column;
}

.rm-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
  white-space: nowrap;
}

.rm-input {
  width: 120px;
}

/* Reserved fixed-width column so the Total-price column stays aligned even when a type has no weight total. */
.rm-other-totals {
  flex: 0 0 130px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px 12px;
  text-align: right;
}

.rm-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* Fixed Total-price column — same horizontal band on every row. */
.rm-total-price {
  flex: 0 0 130px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.rm-price-input {
  width: 120px;
}

.rm-total-value {
  font-weight: 600;
  padding-top: 4px;
  white-space: nowrap;
}

/* Trailing remove column. */
.rm-remove {
  flex: 0 0 40px;
  display: flex;
  justify-content: flex-end;
  padding-top: 14px;
}

.rm-add {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 12px 8px;
}

.rm-add-select {
  flex: 0 0 240px;
  width: 240px;
}
</style>
